import React, { Fragment, useState, useRef, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import authenticationCtx from 'context/authentication';
import balancesCtx from 'context/balances';
import tradesCtx from 'context/trades';
import darkmodeCtx from 'context/darkmode';
import exchangeCtx from 'context/exchange';
import accountCtx from 'context/account';
import floatToText from 'lib/float-to-text';
import { Link, navigate } from 'gatsby';
import { Listbox, Transition } from '@headlessui/react';
import useClickOutside from 'hooks/useClickOutside';
import { headerNavItems, userNavItems, ENVIRONMENT, EXCHANGES, APP_VERSION } from 'config/constants';
import useHasMounted from 'hooks/useHasMounted';
import Logo from 'assets/images/svg/logo.inline.svg';
import { later } from 'lib/timers';
import CheckIcon from 'assets/images/svg/check.svg';

// Custom Logo component to avoid defaultProps warning from generated SVG component
const LogoWrapper = ({ className = '' }) => (
	<svg 
		fill="none" 
		viewBox="0 0 512 512" 
		xmlns="http://www.w3.org/2000/svg" 
		className={className}
	>
		<g fill="#facc15">
			<path d="m355.798 219.763c-19.038 0-34.215 16.456-32.051 35.87 1.83 16.455 16.341 28.556 32.916 28.556h140.319c5.525 0 10.018-4.488 10.018-10.006v-44.38c0-5.552-4.493-10.04-10.051-10.04zm5.425 52.093c-13.813 2.26-25.561-9.475-23.298-23.271 1.364-8.344 8.121-15.092 16.475-16.455 13.812-2.261 25.561 9.474 23.298 23.27-1.365 8.344-8.121 15.093-16.475 16.456z"/>
			<path d="m178.369 140.229c0-7.225-5.864-13.081-13.097-13.081s-13.097 5.856-13.097 13.081c0 7.224 5.864 13.081 13.097 13.081s13.097-5.857 13.097-13.081z"/>
			<path d="m141.824 276.643h-35.279v35.736h35.279z"/>
			<path d="m141.824 338.575h-35.279v35.737h35.279z"/>
			<path d="m141.824 214.744h-35.279v35.736h35.279z"/>
			<path d="m450.886 3h-305.6c-21.401 0-38.741 17.3198-38.741 38.6952v84.9208h35.279v-56.4646c0-6.7816 5.492-12.3 12.315-12.3h287.894c6.789 0 12.314 5.4852 12.314 12.3v134.5196h35.28v-162.9758c0-21.3754-17.341-38.6952-38.741-38.6952zm-100.38 36.7338h-104.84c-5.026 0-9.086-4.0556-9.086-9.0754 0-5.0197 4.06-9.0754 9.086-9.0754h104.84c5.025 0 9.086 4.0557 9.086 9.0754 0 5.0198-4.061 9.0754-9.086 9.0754z"/>
			<path d="m454.347 458.969c0 6.782-5.492 12.3-12.314 12.3h-287.894c-6.79 0-12.315-5.485-12.315-12.3v-58.495h-35.279v69.831c0 21.375 17.34 38.695 38.741 38.695h305.6c21.4 0 38.741-17.32 38.741-38.695v-171.024h-35.28z"/>
			<path d="m141.824 152.812h-35.279v35.736h35.279z"/>
			<path d="m78.5876 126.616h-60.4743c-7.2423 0-13.1133 5.856-13.1133 13.081s5.871 13.081 13.1133 13.081h60.4743c7.2423 0 13.1133-5.856 13.1133-13.081s-5.871-13.081-13.1133-13.081z"/>
			<path d="m78.5876 189.545h-32.2507c-7.2423 0-13.1133 5.857-13.1133 13.082 0 7.224 5.871 13.081 13.1133 13.081h32.2507c7.2423 0 13.1133-5.857 13.1133-13.081 0-7.225-5.871-13.082-13.1133-13.082z"/>
			<path d="m90.6692 263.562c0-7.225-5.8636-13.082-13.0967-13.082s-13.0967 5.857-13.0967 13.082c0 7.224 5.8636 13.081 13.0967 13.081s13.0967-5.857 13.0967-13.081z"/>
			<path d="m78.5543 312.379h-52.386c-7.2423 0-13.1133 5.857-13.1133 13.082 0 7.224 5.871 13.081 13.1133 13.081h52.386c7.2423 0 13.1133-5.857 13.1133-13.081 0-7.225-5.871-13.082-13.1133-13.082z"/>
			<path d="m90.6692 325.493c0-7.225-5.8636-13.082-13.0967-13.082s-13.0967 5.857-13.0967 13.082c0 7.224 5.8636 13.081 13.0967 13.081s13.0967-5.857 13.0967-13.081z"/>
			<path d="m78.5543 374.31h-52.386c-7.2423 0-13.1133 5.857-13.1133 13.082 0 7.224 5.871 13.081 13.1133 13.081h52.386c7.2423 0 13.1133-5.857 13.1133-13.081 0-7.225-5.871-13.082-13.1133-13.082z"/>
			<path d="m90.6692 387.424c0-7.225-5.8636-13.082-13.0967-13.082s-13.0967 5.857-13.0967 13.082c0 7.224 5.8636 13.081 13.0967 13.081s13.0967-5.857 13.0967-13.081z"/>
			<path d="m78.5543 436.241h-52.386c-7.2423 0-13.1133 5.857-13.1133 13.082 0 7.224 5.871 13.081 13.1133 13.081h52.386c7.2423 0 13.1133-5.857 13.1133-13.081 0-7.225-5.871-13.082-13.1133-13.082z"/>
			<path d="m90.6692 449.355c0-7.225-5.8636-13.082-13.0967-13.082s-13.0967 5.857-13.0967 13.082c0 7.224 5.8636 13.081 13.0967 13.081s13.0967-5.857 13.0967-13.081z"/>
			<path d="m78.5543 498.172h-52.386c-7.2423 0-13.1133 5.857-13.1133 13.082 0 7.224 5.871 13.081 13.1133 13.081h52.386c7.2423 0 13.1133-5.857 13.1133-13.081 0-7.225-5.871-13.082-13.1133-13.082z"/>
			<path d="m90.6692 511.286c0-7.225-5.8636-13.082-13.0967-13.082s-13.0967 5.857-13.0967 13.082c0 7.224 5.8636 13.081 13.0967 13.081s13.0967-5.857 13.0967-13.081z"/>
		</g>
	</svg>
);

const EXCHANGES_ARRAY = [];
const EXCHANGES_LABELS = {};

Object.keys(EXCHANGES).forEach(key => {
	const exchangeObject = EXCHANGES[key];
	if (exchangeObject.tradeEnabled) {
		EXCHANGES_ARRAY.push(key);
		EXCHANGES_LABELS[key] = exchangeObject.displayName;
	}
});

const Header = props => {
	const { accountdata } = useContext(accountCtx);
	const { balances } = useContext(balancesCtx);
	const { exchange, exchangeKey, setExchange } = useContext(exchangeCtx);
	const { positions = [], subscribePositions = [], unsubscribePositions = [] } = useContext(tradesCtx);
	const { darkmode, toggleDarkmode } = useContext(darkmodeCtx);
	const hasMounted = useHasMounted();
	const [balanceVisible, setBalanceVisible] = useState(false);
	const [open, setOpen] = useState(false);
	const [openUserManagement, setOpenUserManagement] = useState(false);
	const [showUserToolTip, setShowUserToolTip] = useState(false);
	const { isLoggedIn, logout, scope } = useContext(authenticationCtx);
	const userMenuRef = useRef(null);
	const timerRef = useRef(null);
	const positionsCount = positions.length;

	let developmentLabel;

	useEffect(() => {
		subscribePositions();

		return () => {
			if (timerRef.current) {
				timerRef.current.cancel();
			}
			unsubscribePositions();
		};
	}, []);

	useEffect(() => {
		timerRef.current = later(() => {
			setBalanceVisible(true);
		}, 100);
	}, [exchange]);

	const changeExchange = newExchange => {
		// make sure first to make the balances invisible
		// because the "exchange"-button will change from witdh when the selection is chaned, this would lead into a shift from the balance text, which we want to avoid
		setBalanceVisible(false);
		setExchange(newExchange);
	};

	const navigateTo = url => {
		setOpen(false);
		setOpenUserManagement(false);
		navigate(url);
	};

	const handleLogout = () => {
		setOpenUserManagement(false);
		navigate('/dashboard');
		logout();
	};

	const handleOpenNavMenu = (e, isOpen) => {
		if (e && e.type === 'keydown' && (e.key === 'Tab' || e.key === 'Shift')) {
			return;
		}
		setOpen(!!isOpen);
	};

	const handleOpenUserMenu = (e, isOpen) => {
		if (e && e.type === 'keydown' && (e.key === 'Tab' || e.key === 'Shift')) {
			return;
		}
		setShowUserToolTip(false);
		setOpenUserManagement(!!isOpen);
	};

	useClickOutside([userMenuRef], handleOpenUserMenu);

	if (!hasMounted) {
		return null;
	}

	if (ENVIRONMENT === 'development') {
		developmentLabel = <div className="ml-auto whitespace-nowrap text-3xl text-yellow-300 font-bold">Development version {APP_VERSION}</div>;
	} else {
		developmentLabel = <div className="ml-auto whitespace-nowrap text-xl text-gray-400 font-semibold">Version {APP_VERSION}</div>;
	}

	const logo = (
		<Link to="/dashboard" className="my-auto">
			<LogoWrapper className="h-10" />
		</Link>
	);

	let menuItems;
	let menuItemsUser;
	let avatarOrLogin = (
		<button
			type="button"
			className="bg-white rounded-full px-6 py-1 text-black"
			onClick={() => navigateTo('/dashboard')}
		>
			Login
		</button>
	);

	if (isLoggedIn) {
		menuItems = headerNavItems.map(({ label, url }) => {
			const isDashboardPage = label.toLowerCase() === 'dashboard';
			const showPositions = isDashboardPage && positionsCount > 0;
			const path = window.location.pathname;
			const dashboardPageSelected = path === '/dashboard/';

			let positionsCountNode;
			if (showPositions) {
				positionsCountNode = (
					<span
						className={clsx('py-1 px-2 rounded-full text-center ml-2', {
							'bg-yellow-300 text-black': dashboardPageSelected,
							'bg-blue-500 text-white': !dashboardPageSelected,
						})}
					>
						{positionsCount}
					</span>
				);
			}

			return (
				<Link
					to={url}
					className={clsx(
						'form-input hover:bg-blue-300 hover:text-black w-full font-normal rounded border border-indigo-300 bg-transparent px-4 py-3 text-white placeholder:text-white/25',
						{
							'pr-6': !showPositions,
							'pr-4': showPositions,
						},
					)}
					activeClassName="!bg-blue-500 !text-white !cursor-default"
					key={url}
				>
					{label}
					{positionsCountNode}
				</Link>
			);
		});

		menuItemsUser = userNavItems.map(({ label, url, scope }) => (
			<Link to={url} className="p-4 hover:bg-blue-500 hover:text-white" key={url}>
				{label}
			</Link>
		));

		const fullUserName = '';
		menuItemsUser.push(
			<button type="button" className="p-4 hover:bg-blue-500 hover:text-white" onClick={handleLogout} key="logout">
				Logout
			</button>,
		);
		avatarOrLogin = (
			<div className="relative" ref={userMenuRef}>
				<button
					type="button"
					className="border border-white p-1 rounded"
					onClick={e => handleOpenUserMenu(e, !openUserManagement)}
				>
					<img alt={fullUserName} src="/images/avatar.png" width="40" height="40" />
				</button>

				<div
					onClick={e => handleOpenUserMenu(e, false)}
					className={clsx(
						'flex flex-col mt-0 shadow-xl rounded absolute z-20 bg-white text-black right-0 whitespace-nowrap',
						{
							'invisible opacity-0': !openUserManagement,
							'visible opacity-100': openUserManagement,
						},
					)}
				>
					{menuItemsUser}
				</div>
			</div>
		);
	}

	menuItems = <nav className="flex space-x-4">{menuItems}</nav>;

	const hamburgerMenu = (
		<>
			<button onClick={event => handleOpenNavMenu(event, true)}>Menu</button>
			{menuItems}
		</>
	);

	const handleCloseUserToolTip = () => {
		setShowUserToolTip(false);
	};

	const handleOpenUserToolTip = () => {
		if (!openUserManagement) {
			setShowUserToolTip(true);
		}
	};

	let balanceNode;
	if (isLoggedIn && balanceVisible && balances[0]) {
		balanceNode = floatToText(balances[0].balance, { append: 'balance: ', append: ' USD' });
	}

	const usedExchanges = EXCHANGES_ARRAY.filter(key => accountdata?.apikeys[key]);

	const selectExchangeList = (
		<div>
			<Listbox value={exchangeKey} onChange={changeExchange} className="flex flex-col gap-1 font-semibold">
				<div className="relative">
					<Listbox.Button
						className={clsx(
							'form-input w-full font-normal rounded border border-indigo-300 bg-transparent px-4 py-3 text-white placeholder:text-white/25',
							{
								'cursor-default': usedExchanges.length < 2,
								'hover:bg-blue-300 hover:text-black': usedExchanges.length > 1,
							},
						)}
						onClick={e => {
							if (usedExchanges.length < 2) {
								e.preventDefault(); // Prevent interaction when disabled
							}
						}}
					>
						<span className="block truncate text-left uppercase">{exchangeKey}</span>
					</Listbox.Button>

					<Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
						<Listbox.Options className="absolute right-0 mt-12 w-300 overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
							{usedExchanges.map(exchange => (
								<Listbox.Option
									className={({ active }) =>
										`relative cursor-default select-none py-2 pl-10 pr-4 cursor-pointer ${
											active ? 'bg-amber-100 text-amber-900' : 'text-gray-900'
										}`
									}
									value={exchange}
									key={exchange}
								>
									{({ selected }) => (
										<>
											<span className={`block truncate uppercase ${selected ? 'font-medium' : 'font-normal'}`}>
												{EXCHANGES_LABELS[exchange]}
											</span>
											{selected ? (
												<span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
													<CheckIcon className="text-yellow-400 w-6 h-6" />
												</span>
											) : null}
										</>
									)}
								</Listbox.Option>
							))}
						</Listbox.Options>
					</Transition>
				</div>
			</Listbox>
		</div>
	);

	return (
		<header className="container-fluid py-2 bg-black-42 border-b border-white">
			<div className="container flex items-center place-content-between">
				<div className="container flex justify-start items-center gap-6">
					{logo}
					{menuItems}
				</div>
				<div>{developmentLabel}</div>
				<div className="container flex justify-end items-center gap-6">
					<div className="text-xl">
						<span className="text-blue-400 font-bold">{balanceNode}</span>
					</div>
					{selectExchangeList}
					{avatarOrLogin}
				</div>
			</div>
		</header>
	);
};

Header.propTypes = {
	uri: PropTypes.string.isRequired,
};

export default Header;
